import { useCallback, useMemo, useState } from "react";
import dayjs from "newui/utils/dayjs";
import { RfxDocumentFilters, RfxDocumentRow } from "../types/rfx-document.types";

const DEFAULT_FILTERS: RfxDocumentFilters = {
  filename: "",
  categoryIds: [],
  dateRange: { from: null, to: null },
  uploadedByIds: [],
  revision: "",
};

export const useDocumentFilters = () => {
  const [filters, setFilters] = useState<RfxDocumentFilters>(DEFAULT_FILTERS);

  const setFilter = useCallback(
    <K extends keyof RfxDocumentFilters>(key: K, value: RfxDocumentFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      (filters.filename ?? "") !== "" ||
      (filters.categoryIds?.length ?? 0) > 0 ||
      (filters.dateRange?.from ?? null) !== null ||
      (filters.dateRange?.to ?? null) !== null ||
      (filters.uploadedByIds?.length ?? 0) > 0 ||
      (filters.revision ?? "") !== "",
    [filters]
  );

  const filterDocuments = useCallback(
    (docs: RfxDocumentRow[]): RfxDocumentRow[] => {
      return docs.filter((doc) => {
        if (filters.filename) {
          const needle = filters.filename.toLowerCase().trim();
          // Strip a leading dot so users can type ".pdf" or "pdf" interchangeably.
          const extNeedle = needle.startsWith(".") ? needle.slice(1) : needle;
          const fileName = doc.fileName.toLowerCase();
          const ext = fileName.split(".").pop() ?? "";
          const matchesName = fileName.includes(needle);
          const matchesExt = extNeedle.length > 0 && ext === extNeedle;
          if (!matchesName && !matchesExt) {
            return false;
          }
        }

        const categoryIds = filters.categoryIds ?? [];
        if (categoryIds.length > 0 && !categoryIds.includes(doc.categoryId)) {
          return false;
        }

        if (filters.dateRange?.from) {
          const docDate = dayjs(doc.createdAt);
          if (docDate.isBefore(dayjs(filters.dateRange.from), "day")) {
            return false;
          }
        }

        if (filters.dateRange?.to) {
          const docDate = dayjs(doc.createdAt);
          if (docDate.isAfter(dayjs(filters.dateRange.to), "day")) {
            return false;
          }
        }

        const uploadedByIds = filters.uploadedByIds ?? [];
        if (uploadedByIds.length > 0 && !uploadedByIds.includes(doc.uploadedById)) {
          return false;
        }

        if (
          filters.revision &&
          !doc.externalRevision.toLowerCase().includes(filters.revision.toLowerCase())
        ) {
          return false;
        }

        return true;
      });
    },
    [filters]
  );

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    filterDocuments,
  };
};
